import jwtDecode from 'jwt-decode';

export type IntercomCompany = {
  company_id: string;
  name?: string;
  created_at?: number;
};

export type IntercomOrganizationProfile = {
  id?: string;
  displayName?: string;
  organizationName?: string;
  created?: string | Date | null;
} | null;

export type IntercomBootArgs = {
  app_id: string;
  intercom_user_jwt?: string;
  company?: IntercomCompany;
};

// Refresh the JWT slightly before it actually expires so we never boot with a dead token.
const EXPIRY_BUFFER_SECONDS = 60;

export const toUnixSeconds = (value?: string | Date | null): number | undefined => {
  if (!value) return undefined;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? undefined : Math.floor(ms / 1000);
};

export const buildIntercomCompany = (
  organizationProfile?: IntercomOrganizationProfile
): IntercomCompany | undefined => {
  if (!organizationProfile?.id) return undefined;

  return {
    company_id: organizationProfile.id,
    name: organizationProfile.displayName || organizationProfile.organizationName || undefined,
    created_at: toUnixSeconds(organizationProfile.created)
  };
};

// User identity (user_id, email, name, created_at) travels only inside the backend-signed JWT.
// Without a JWT we boot anonymously rather than sending unsigned user data Intercom can't trust.
export const buildIntercomBootArgs = (
  appId: string,
  intercomUserJwt?: string | null,
  organizationProfile?: IntercomOrganizationProfile
): IntercomBootArgs => {
  if (!intercomUserJwt) {
    return { app_id: appId };
  }

  return {
    app_id: appId,
    intercom_user_jwt: intercomUserJwt,
    company: buildIntercomCompany(organizationProfile)
  };
};

export const isIntercomTokenUsable = (token: string | null | undefined, nowMs: number): boolean => {
  if (!token) return false;

  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (!exp) return true;
    return exp - EXPIRY_BUFFER_SECONDS > nowMs / 1000;
  } catch {
    return false;
  }
};